<template>
  <div
    class="dm-conv-card"
    :class="{ unread: conversation.unread }"
  >
    <div
      class="dm-conv-main"
      :class="{ clickable: linkToTimeline }"
      @click="openConversation"
    >
      <div class="dm-avatar">
        <UserAvatar
          v-if="primaryMember"
          :user="primaryMember"
          :compact="false"
        />

        <span
          v-if="conversation.unread"
          class="dm-unread-dot"
          title="Unread message"
        />
      </div>

      <div class="dm-conv-content">
        <div class="dm-conv-topline">
          <div class="dm-identity">
            <strong class="dm-name">
              {{ conversationName }}
            </strong>

            <span
              v-if="conversationHandle"
              class="dm-handle"
            >
              {{ conversationHandle }}
            </span>
          </div>

          <span class="dm-time">
            {{ lastMessageTime }}
          </span>
        </div>

        <div
          v-if="showLastStatus"
          class="dm-preview"
        >
          {{ lastMessage }}
        </div>

        <div
          v-if="otherMembers.length > 1"
          class="dm-group-count"
        >
          {{ otherMembers.length }} participants
        </div>
      </div>

      <button
        class="button-unstyled dm-delete"
        :title="$t('dm_conv.delete_tooltip')"
        @click.stop="deleteConversation"
      >
        <FAIcon icon="trash-alt" />
      </button>
    </div>

    <div
      v-if="showFullControls"
      class="dm-full-controls"
    >
      <button
        class="btn button-default"
        :title="$t('dm_conv.recipients_edit_mode_button_tooltip')"
        @click.once="$router.push({
          name: 'dm-conversation-recipients',
          params: { id: conversation.id }
        })"
      >
        {{ $t('dm_conv.recipients_edit_mode_button') }}
      </button>
    </div>

    <teleport to="#modal">
      <confirm-modal
        v-if="showingDeleteConfirmDialogue"
        :title="$t('dm_conv.delete_confirm_title')"
        :confirm-text="$t('dm_conv.delete_confirm_accept_button')"
        :cancel-text="$t('dm_conv.delete_confirm_cancel_button')"
        @accepted="doDeleteConversation"
        @cancelled="hideDeleteConfirmModal"
      >
        {{ $t('dm_conv.delete_confirm', { identifier: conversation.id }) }}
      </confirm-modal>
    </teleport>
  </div>
</template>

<script src="./dm_conv_card.js"></script>

<style lang="scss">
@import '../../_variables.scss';

.dm-conv-card {
  position: relative;
  border-bottom: 1px solid var(--border, $fallback--border);

  &:last-child {
    border-bottom: 0;
  }

  &.unread {
    background: color-mix(in srgb, var(--link) 7%, transparent);

    .dm-name,
    .dm-preview {
      font-weight: 700;
    }
  }

  .dm-conv-main {
    min-height: 72px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;

    &.clickable {
      cursor: pointer;
    }

    &.clickable:hover {
      background: var(--selectedMenu, rgba(255, 255, 255, 0.04));
    }
  }

  .dm-avatar {
    position: relative;
    flex: 0 0 auto;

    .Avatar {
      width: 48px;
      height: 48px;
    }
  }

  .dm-unread-dot {
    position: absolute;
    right: -1px;
    bottom: 1px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--link);
    border: 2px solid var(--bg, $fallback--bg);
  }

  .dm-conv-content {
    flex: 1 1 auto;
    min-width: 0;
  }

  .dm-conv-topline {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }

  .dm-identity {
    min-width: 0;
    display: flex;
    align-items: baseline;
    gap: 6px;
  }

  .dm-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dm-handle,
  .dm-time,
  .dm-group-count {
    color: var(--faint);
    font-size: 0.88em;
  }

  .dm-handle {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dm-time {
    flex: 0 0 auto;
  }

  .dm-preview {
    margin-top: 5px;
    color: var(--text);
    opacity: 0.8;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dm-group-count {
    margin-top: 3px;
  }

  .dm-delete {
    flex: 0 0 auto;
    padding: 8px;
    opacity: 0.45;

    &:hover {
      opacity: 1;
    }
  }

  .dm-full-controls {
    padding: 8px 14px 14px;
    text-align: center;
  }

  @media (max-width: 600px) {
    .dm-conv-main {
      padding: 10px;
      gap: 10px;
    }

    .dm-avatar .Avatar {
      width: 44px;
      height: 44px;
    }

    .dm-handle {
      display: none;
    }

    .dm-delete {
      padding: 6px;
    }
  }
}
</style>
